<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import {
  ArrowLeft,
  Files,
  Layers,
  ListTodo,
  Lock,
  NotebookPen,
  Pencil,
  Trash2,
  UsersRound,
} from '@lucide/vue';
import { StatoFase, faseUpdateSchema, type Fase } from '@evolfin/shared';
import { useAvvisi } from '@/composables/useAvvisi';
import { useConferma } from '@/composables/useConferma';
import { useInvalidation } from '@/composables/useInvalidation';
import { useActiveTab } from '@/composables/useActiveTab';
import { useActiveTabScroll } from '@/composables/useActiveTabScroll';
import { EvolfinApiError } from '@/api/client';
import CampoTesto from '@/components/ui/CampoTesto.vue';
import CampoData from '@/components/ui/CampoData.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import DialogModale from '@/components/ui/DialogModale.vue';
import StatoErrore from '@/components/ui/StatoErrore.vue';
import StatoVuoto from '@/components/ui/StatoVuoto.vue';
import FaseFileSezione from '../../../components/FaseFileSezione.vue';
import VerbaleFaseAzione from '../../../components/VerbaleFaseAzione.vue';
import PillStatoFase from '../../../components/PillStatoFase.vue';
import VistaTask from '@/features/task/components/VistaTask.vue';
import { formattaData, formattaDataOra, formattaEuro } from '@/utils/formato';
import { projectKeys } from '../../../query-keys';
import {
  aggiornaFase,
  creaNotaFase,
  dettaglioFase,
  dettaglioProgetto,
  eliminaFase,
  listaNoteProgetto,
  listaUtentiProgetto,
} from '../../../api';

definePage({ meta: { titolo: 'Progetti' } });

const route = useRoute();
const router = useRouter();
const avvisi = useAvvisi();
const conferma = useConferma();
const invalidate = useInvalidation('progetti');

const progettoId = computed(() => Number((route.params as { id: string }).id));
const faseId = computed(() => Number((route.params as { faseId: string }).faseId));

const {
  data: fase,
  isLoading,
  isError,
  refetch,
} = useQuery({
  queryKey: computed(() => projectKeys.phase(progettoId.value, faseId.value)),
  queryFn: () => dettaglioFase(progettoId.value, faseId.value),
  // È la pagina su cui si lavora: rimontandola si riparte dal server.
  refetchOnMount: 'always',
});

const { data: progetto } = useQuery({
  queryKey: computed(() => projectKeys.detail(progettoId.value)),
  queryFn: () => dettaglioProgetto(progettoId.value),
});

const { data: utentiTeam } = useQuery({
  queryKey: computed(() => projectKeys.section(progettoId.value, 'utenti')),
  queryFn: () => listaUtentiProgetto(progettoId.value),
});

const { data: noteProgetto, isLoading: noteInCaricamento } = useQuery({
  queryKey: computed(() => projectKeys.section(progettoId.value, 'note')),
  queryFn: () => listaNoteProgetto(progettoId.value),
});
const note = computed(() => noteProgetto.value?.filter((n) => n.faseProgettoId === faseId.value));

function tornaAlleFasi(): Promise<unknown> {
  return router.push({ path: `/progetti/${progettoId.value}`, query: { tab: 'fasi' } });
}

/* ── Assegnatari: aggiunta/rimozione diretta, senza dialog ───────────────── */
const utentiNonAssegnati = computed(
  () => utentiTeam.value?.filter((u) => !fase.value?.assegnatarioIds.includes(u.id)) ?? [],
);

async function aggiungiAssegnatario(evento: Event): Promise<void> {
  const select = evento.target as HTMLSelectElement;
  const utenteId = Number(select.value);
  select.value = '';
  if (!utenteId || !fase.value) return;
  try {
    await aggiornaFase(progettoId.value, faseId.value, {
      assegnatarioIds: [...fase.value.assegnatarioIds, utenteId],
    });
    await invalidate();
  } catch (errore) {
    const m = errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile assegnare', m);
  }
}

async function rimuoviAssegnatario(utenteId: number): Promise<void> {
  if (!fase.value) return;
  try {
    await aggiornaFase(progettoId.value, faseId.value, {
      assegnatarioIds: fase.value.assegnatarioIds.filter((id) => id !== utenteId),
    });
    await invalidate();
  } catch (errore) {
    const m = errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile rimuovere', m);
  }
}

function nomeUtente(utenteId: number): string {
  const u = utentiTeam.value?.find((u) => u.id === utenteId);
  return u ? `${u.nome} ${u.cognome}` : 'Utente';
}
function iniziali(utenteId: number): string {
  const u = utentiTeam.value?.find((u) => u.id === utenteId);
  return u ? `${u.nome.at(0) ?? ''}${u.cognome.at(0) ?? ''}`.toUpperCase() : '?';
}

/* ── Modifica ─────────────────────────────────────────────────────────── */
const modificaAperta = ref(false);
const salvataggio = ref(false);
const erroreGenerale = ref<string | null>(null);
const erroriCampo = ref<Record<string, string[]>>({});
const primoErrore = (k: string) => erroriCampo.value[k]?.[0];

const form = reactive({
  nome: '',
  descrizione: '',
  dataInizio: '',
  dataFine: '',
  stato: 'Aperta' as Fase['stato'],
  valoreFase: '',
  numeroMarcheDaBollo: '',
  riservata: false,
});

function apriModifica(): void {
  if (!fase.value) return;
  form.nome = fase.value.nome;
  form.descrizione = fase.value.descrizione ?? '';
  form.dataInizio = fase.value.dataInizio ?? '';
  form.dataFine = fase.value.dataFine ?? '';
  form.stato = fase.value.stato;
  form.valoreFase = fase.value.valoreFase != null ? String(fase.value.valoreFase) : '';
  form.numeroMarcheDaBollo =
    fase.value.numeroMarcheDaBollo != null ? String(fase.value.numeroMarcheDaBollo) : '';
  form.riservata = fase.value.riservata;
  erroriCampo.value = {};
  erroreGenerale.value = null;
  modificaAperta.value = true;
}

function raccogliErrori(issues: readonly { path: PropertyKey[]; message: string }[]): void {
  for (const i of issues) {
    (erroriCampo.value[i.path.map(String).join('.')] ??= []).push(i.message);
  }
}

async function salvaModifica(): Promise<void> {
  erroriCampo.value = {};
  erroreGenerale.value = null;
  const payload = {
    nome: form.nome,
    descrizione: form.descrizione || undefined,
    dataInizio: form.dataInizio || undefined,
    dataFine: form.dataFine || undefined,
    stato: form.stato,
    valoreFase: form.valoreFase ? Number(form.valoreFase) : undefined,
    numeroMarcheDaBollo: form.numeroMarcheDaBollo ? Number(form.numeroMarcheDaBollo) : undefined,
    riservata: form.riservata,
  };
  const esito = faseUpdateSchema.safeParse(payload);
  if (!esito.success) {
    raccogliErrori(esito.error.issues);
    return;
  }
  salvataggio.value = true;
  try {
    await aggiornaFase(progettoId.value, faseId.value, esito.data);
    modificaAperta.value = false;
    avvisi.successo('Fase aggiornata');
    await invalidate();
  } catch (errore) {
    erroreGenerale.value =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
  } finally {
    salvataggio.value = false;
  }
}

watch(modificaAperta, (ora) => {
  if (!ora) erroriCampo.value = {};
});

async function elimina(): Promise<void> {
  if (!fase.value) return;
  if (
    !(await conferma({
      titolo: 'Elimina fase',
      messaggio: `Eliminare la fase "${fase.value.nome}"? L'operazione è definitiva.`,
      etichettaConferma: 'Elimina',
      distruttiva: true,
    }))
  ) {
    return;
  }
  try {
    await eliminaFase(progettoId.value, faseId.value);
    avvisi.successo('Fase eliminata');
    // Prima si esce dalla fase, poi si invalida: restando qui, l'invalidazione
    // rimetterebbe in coda anche la query di QUESTA fase, che ora risponde 404.
    await tornaAlleFasi();
    await invalidate();
  } catch (errore) {
    const m = errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile eliminare', m);
  }
}

/* ── Note (create qui: la fase è il punto primario di scrittura) ───── */
const testoNota = ref('');
const salvataggioNota = ref(false);

async function aggiungiNota(): Promise<void> {
  if (!testoNota.value.trim()) return;
  salvataggioNota.value = true;
  try {
    await creaNotaFase(progettoId.value, faseId.value, { testo: testoNota.value });
    testoNota.value = '';
    await invalidate();
  } catch (errore) {
    const m = errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile aggiungere la nota', m);
  } finally {
    salvataggioNota.value = false;
  }
}

/** La scheda File sparisce se i documenti della fase non sono accessibili. */
const schede = computed(() => [
  { chiave: 'panoramica' as const, etichetta: 'Panoramica', icona: Layers },
  ...(fase.value?.accessoFile !== false
    ? [{ chiave: 'file' as const, etichetta: 'File', icona: Files }]
    : []),
  { chiave: 'task' as const, etichetta: 'Task', icona: ListTodo },
]);

const tab = useActiveTab(['panoramica', 'file', 'task'] as const, 'panoramica');
useActiveTabScroll(tab);
</script>

<template>
  <div class="flex flex-col gap-6">
    <button
      type="button"
      class="flex w-fit items-center gap-1.5 text-[13px] text-ink-secondary transition-colors hover:text-ink"
      @click="tornaAlleFasi"
    >
      <ArrowLeft :size="15" :stroke-width="1.9" />
      {{ progetto?.nome ?? 'Progetto' }}
    </button>

    <div v-if="isLoading" class="text-[13px] text-ink-muted">Caricamento…</div>

    <StatoErrore
      v-else-if="isError || !fase"
      titolo="Fase non trovata"
      messaggio="La fase richiesta non esiste o non è più disponibile."
      @riprova="refetch()"
    />

    <template v-else>
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <span
            class="grid size-12 shrink-0 place-items-center rounded-lg bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
            aria-hidden="true"
          >
            <Lock v-if="fase.riservata && !fase.accessibile" :size="22" :stroke-width="1.8" />
            <Layers v-else :size="22" :stroke-width="1.8" />
          </span>
          <div>
            <div class="flex flex-wrap items-center gap-2.5">
              <h1 class="font-display text-[26px] leading-tight font-semibold break-words">
                {{ fase.nome }}
              </h1>
              <PillStatoFase :stato="fase.stato" />
              <span
                v-if="fase.riservata"
                class="rounded-pill bg-[var(--surface-warning-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-warning-text)]"
              >
                Riservata
              </span>
            </div>
          </div>
        </div>
        <div v-if="fase.accessibile" class="flex shrink-0 flex-wrap items-center gap-2">
          <!-- Il verbale finisce fra i file della fase: senza accesso a quelli,
               non è un'azione che si possa offrire. -->
          <VerbaleFaseAzione v-if="fase.accessoFile" :progetto-id="progettoId" :fase-id="faseId" />
          <PulsanteAzione
            variante="contorno"
            :disabilitato="!progetto?.regoleFasi.permettiModificaFasi"
            :title="
              !progetto?.regoleFasi.permettiModificaFasi
                ? 'Il progetto non consente la modifica delle fasi'
                : undefined
            "
            @click="apriModifica"
          >
            Modifica
            <template #icona><Pencil :size="15" :stroke-width="1.9" /></template>
          </PulsanteAzione>
          <PulsanteAzione
            variante="contorno"
            :disabilitato="!progetto?.regoleFasi.permettiEliminazioneFasi"
            :title="
              !progetto?.regoleFasi.permettiEliminazioneFasi
                ? 'Il progetto non consente l\'eliminazione delle fasi'
                : undefined
            "
            @click="elimina"
          >
            Elimina
            <template #icona><Trash2 :size="15" :stroke-width="1.9" /></template>
          </PulsanteAzione>
        </div>
      </header>

      <template v-if="!fase.accessibile">
        <StatoVuoto
          :icona="Lock"
          titolo="Dati non accessibili"
          descrizione="Questa fase è riservata: non hai il privilegio per vederne i dettagli."
        />
      </template>

      <template v-else>
        <!-- Barra tab -->
        <nav
          ref="tabBar"
          class="flex gap-1 overflow-x-auto border-b border-line-soft"
          aria-label="Sezioni fase"
        >
          <button
            v-for="t in schede"
            :key="t.chiave"
            type="button"
            class="flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors"
            :aria-current="tab === t.chiave ? 'true' : undefined"
            :class="
              tab === t.chiave
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-ink-secondary hover:text-ink'
            "
            @click="tab = t.chiave"
          >
            <component :is="t.icona" :size="15" :stroke-width="1.8" />
            {{ t.etichetta }}
          </button>
        </nav>

        <div v-if="tab === 'panoramica'" class="grid gap-5 lg:grid-cols-[3fr_2fr] lg:items-start">
          <div class="flex flex-col gap-5">
            <!-- Dettagli -->
            <div class="card flex flex-col gap-4 p-5">
              <h2 class="text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase">
                Dettagli
              </h2>
              <div class="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                <div>
                  <p class="text-[12px] text-ink-muted">Inizio</p>
                  <p class="mt-0.5 font-medium text-ink">
                    {{ fase.dataInizio ? formattaData(fase.dataInizio) : '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-[12px] text-ink-muted">Fine</p>
                  <p class="mt-0.5 font-medium text-ink">
                    {{ fase.dataFine ? formattaData(fase.dataFine) : '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-[12px] text-ink-muted">Valore fase</p>
                  <!-- Un trattino direbbe "non c'è". Qui invece c'è e non si può
                       vedere: sono due cose diverse e vanno dette diversamente. -->
                  <p
                    v-if="!fase.accessoValori"
                    class="mt-0.5 flex items-center gap-1 text-[13px] text-ink-muted"
                  >
                    <Lock :size="13" :stroke-width="1.9" aria-hidden="true" /> Non visibile
                  </p>
                  <p v-else class="mt-0.5 font-medium text-ink tabular-nums">
                    {{ fase.valoreFase != null ? formattaEuro(fase.valoreFase) : '—' }}
                  </p>
                </div>
                <div>
                  <p class="text-[12px] text-ink-muted">Marche da bollo</p>
                  <p
                    v-if="!fase.accessoValori"
                    class="mt-0.5 flex items-center gap-1 text-[13px] text-ink-muted"
                  >
                    <Lock :size="13" :stroke-width="1.9" aria-hidden="true" /> Non visibile
                  </p>
                  <p v-else class="mt-0.5 font-medium text-ink tabular-nums">
                    {{ fase.numeroMarcheDaBollo ?? '—' }}
                  </p>
                </div>
              </div>
              <p
                v-if="fase.descrizione"
                class="border-t border-line-soft pt-3 text-[13px] text-ink-secondary"
              >
                {{ fase.descrizione }}
              </p>
            </div>

            <!-- Note della fase -->
            <div class="card flex flex-col gap-3 p-5">
              <h2
                class="flex items-center gap-2 text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase"
              >
                <NotebookPen :size="14" :stroke-width="1.9" /> Note
              </h2>
              <div v-if="noteInCaricamento" class="text-[13px] text-ink-muted">Caricamento…</div>
              <StatoVuoto v-else-if="!note?.length" :icona="NotebookPen" titolo="Nessuna nota" />
              <ul v-else class="flex flex-col gap-2.5">
                <li v-for="n in note" :key="n.id" class="rounded-md bg-soft p-3">
                  <p class="text-[13px] whitespace-pre-wrap text-ink">{{ n.testo }}</p>
                  <p class="mt-1.5 text-[11.5px] text-ink-muted">
                    <span v-if="n.autore">{{ n.autore }}</span>
                    <span> · {{ formattaDataOra(n.creatoIl) }}</span>
                  </p>
                </li>
              </ul>
              <form class="flex flex-col gap-2" novalidate @submit.prevent="aggiungiNota">
                <textarea
                  v-model="testoNota"
                  rows="2"
                  placeholder="Scrivi una nota su questa fase…"
                  class="w-full resize-none rounded-sm border border-line bg-surface px-3 py-2 text-[13px]"
                />
                <PulsanteAzione
                  tipo="submit"
                  :disabilitato="!testoNota.trim()"
                  :caricamento="salvataggioNota"
                  class="self-end"
                >
                  Aggiungi
                </PulsanteAzione>
              </form>
            </div>
          </div>

          <div class="flex flex-col gap-5">
            <!-- Assegnatari -->
            <div class="card flex flex-col gap-3 p-5">
              <h2
                class="flex items-center gap-2 text-[11px] font-semibold tracking-[0.07em] text-ink-muted uppercase"
              >
                <UsersRound :size="14" :stroke-width="1.9" /> Assegnatari
              </h2>
              <StatoVuoto
                v-if="!fase.assegnatarioIds.length"
                :icona="UsersRound"
                titolo="Nessun assegnatario"
              />
              <div v-else class="flex flex-col gap-2">
                <span
                  v-for="uid in fase.assegnatarioIds"
                  :key="uid"
                  class="flex items-center gap-2 rounded-pill bg-soft py-1 pr-2 pl-1"
                >
                  <span
                    class="grid size-6 place-items-center rounded-full bg-[var(--color-primary)] text-[10px] font-semibold text-[var(--color-on-primary)]"
                    aria-hidden="true"
                  >
                    {{ iniziali(uid) }}
                  </span>
                  <span class="flex-1 text-[12.5px] text-ink">{{ nomeUtente(uid) }}</span>
                  <button
                    type="button"
                    class="rounded-full p-1 text-ink-muted transition-colors hover:bg-surface hover:text-[var(--color-danger-text)]"
                    title="Rimuovi assegnatario"
                    @click="rimuoviAssegnatario(uid)"
                  >
                    ×
                  </button>
                </span>
              </div>
              <select
                v-if="utentiNonAssegnati.length"
                class="rounded-sm border border-line bg-surface px-3 py-2 text-[13px]"
                @change="aggiungiAssegnatario"
              >
                <option value="">Aggiungi assegnatario…</option>
                <option v-for="u in utentiNonAssegnati" :key="u.id" :value="u.id">
                  {{ u.nome }} {{ u.cognome }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Anche via link diretto (`?tab=file`): la scheda sparita dalla barra
             non deve restare raggiungibile scrivendo l'URL a mano. -->
        <FaseFileSezione
          v-else-if="tab === 'file' && fase.accessoFile"
          :progetto-id="progettoId"
          :fase-id="faseId"
          :nome-fase="fase.nome"
        />
        <VistaTask
          v-else-if="tab === 'task' && progetto && fase"
          ambito="progetto"
          :progetto-id="progettoId"
          :fase-progetto-id="faseId"
          :progetto="{ id: progetto.id, nome: progetto.nome }"
          :fase="{ id: fase.id, nome: fase.nome }"
          testo-vuoto="Nessuna attività aperta su questa fase."
        />
      </template>
    </template>

    <!-- Dialog modifica -->
    <DialogModale v-model="modificaAperta" titolo="Modifica fase" larghezza="34rem">
      <form class="flex flex-col gap-4" novalidate @submit.prevent="salvaModifica">
        <CampoTesto v-model="form.nome" etichetta="Nome" richiesto :errore="primoErrore('nome')" />
        <CampoTesto v-model="form.descrizione" etichetta="Descrizione" />
        <div class="grid grid-cols-2 gap-3">
          <CampoData v-model="form.dataInizio" etichetta="Data inizio" />
          <CampoData v-model="form.dataFine" etichetta="Data fine" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-[11px] font-semibold tracking-[0.07em] text-ink-secondary uppercase"
            >Stato</label
          >
          <select
            v-model="form.stato"
            class="rounded-sm border border-line bg-surface px-3 py-2 text-[13px]"
          >
            <option v-for="s in StatoFase.options" :key="s" :value="s">{{ s }}</option>
          </select>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <CampoTesto
            v-model="form.valoreFase"
            etichetta="Valore fase (€)"
            inputmode="numeric"
            tabulare
          />
          <CampoTesto
            v-model="form.numeroMarcheDaBollo"
            etichetta="N. marche da bollo"
            inputmode="numeric"
            tabulare
          />
        </div>
        <label class="flex items-center gap-2 text-[13px] text-ink">
          <input v-model="form.riservata" type="checkbox" />
          Fase riservata (valori e file visibili solo a chi ha il privilegio dedicato)
        </label>
        <p v-if="erroreGenerale" class="text-[12.5px] text-[var(--color-danger-text)]">
          {{ erroreGenerale }}
        </p>
      </form>
      <template #footer>
        <PulsanteAzione variante="silenzioso" @click="modificaAperta = false"
          >Annulla</PulsanteAzione
        >
        <PulsanteAzione :caricamento="salvataggio" @click="salvaModifica">Salva</PulsanteAzione>
      </template>
    </DialogModale>
  </div>
</template>
